import { Controller } from 'stimulus'

// Photos and files on a result's file column (mobile/tasks/_files, _thumbs).
//
// Take photo and Add file put a tile in the grid at once, with the photo and a
// progress bar. Photos are shrunk on the phone first (longest side MAX_EDGE,
// JPEG, upright, no EXIF), which also turns an iPhone's HEIC into a JPEG the
// server can read. Then each file goes up in turn, one at a time across the
// page, the desktop grid's way: GET /presign, POST the file to the storage
// bucket's cache, then POST the cached file's data to data-mobile-photos-url
// (Mobile::ValueAttachmentsController), which attaches it, recalculates the row,
// and answers JSON. A failed step marks the tile "Not saved · Retry".
//
// Each upload and delete is reported to mobile_entry_controller.js (the
// mobile-entry:track event), so the page's save bar counts it, Retry all
// retries it, and leaving the page waits for it.
//
// Tapping a thumbnail opens a full-screen viewer: swipe or Previous / Next,
// and Delete when the thumbnail carries a delete URL (people who may enter
// results; the server checks again).
const MAX_EDGE = 2000
const JPEG_QUALITY = 0.85
const SHRINKABLE = /^image\/(jpeg|png|webp|heic|heif|bmp)$/i
const HEIC = /\.(heic|heif)$/i
const SWIPE_PX = 50
const SAVED_FADE_MS = 2500
const SIGNED_OUT = 'You were signed out. Sign in again in another tab, then tap Retry.'
const FILE_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M14 3H6a1 1 0 00-1 1v16a1 1 0 001 1h12a1 1 0 001-1V8z"/><path d="M14 3v5h5"/></svg>'

// One upload at a time across the page: several files landing on one task at
// once have deadlocked on the task's touch chain (AttachmentUploader).
let queue = Promise.resolve()

// --- shrinking photos ------------------------------------------------------

function decodeWithImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => { URL.revokeObjectURL(url); resolve(image) }
    image.onerror = () => { URL.revokeObjectURL(url); reject(new Error('unreadable')) }
    image.src = url
  })
}

// Upright as the camera held it: createImageBitmap applies the EXIF
// orientation when asked, and an <img> does by default.
function decode(file) {
  if (!window.createImageBitmap) return decodeWithImage(file)
  return window.createImageBitmap(file, { imageOrientation: 'from-image' }).catch(() => decodeWithImage(file))
}

function jpegName(name) {
  return `${name.replace(/\.[^.]*$/, '') || 'photo'}.jpg`
}

// The photo as a JPEG no larger than MAX_EDGE, or the file itself when it
// isn't a photo or this browser can't read it (the server still takes it).
async function shrink(file) {
  if (!SHRINKABLE.test(file.type) && !HEIC.test(file.name)) return file

  let source
  try {
    source = await decode(file)
  } catch (e) {
    return file
  }
  const width = source.naturalWidth || source.width
  const height = source.naturalHeight || source.height
  const scale = Math.min(1, MAX_EDGE / Math.max(width, height))
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(width * scale)
  canvas.height = Math.round(height * scale)
  const context = canvas.getContext('2d')
  context.fillStyle = '#fff'
  context.fillRect(0, 0, canvas.width, canvas.height)
  context.drawImage(source, 0, 0, canvas.width, canvas.height)
  if (source.close) source.close()

  const blob = await new Promise((resolve) => { canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY) })
  return blob ? new File([blob], jpegName(file.name), { type: 'image/jpeg' }) : file
}

// --- requests ----------------------------------------------------------------

// A request that hangs fails after this long, so it shows Retry and doesn't
// hold up the uploads queued behind it or the wait before leaving the page.
const REQUEST_TIMEOUT_MS = 30000
const UPLOAD_TIMEOUT_MS = 120000

function timeoutSignal(ms) {
  const controller = new AbortController()
  setTimeout(() => controller.abort(), ms)
  return controller.signal
}

// JSON in, JSON out, never following a redirect: here one means the session
// or form token is gone (as in mobile_entry_controller.js).
function requestJSON(url, method, body) {
  const token = document.querySelector('meta[name="csrf-token"]')
  return fetch(url, {
    method,
    credentials: 'same-origin',
    redirect: 'manual',
    signal: timeoutSignal(REQUEST_TIMEOUT_MS),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': token ? token.content : ''
    },
    body: body ? JSON.stringify(body) : undefined
  }).then((response) => {
    if (response.type === 'opaqueredirect' || response.redirected) return { ok: false, status: 0, data: {} }
    return response.json().catch(() => null)
      .then(data => ({ ok: response.ok && data !== null, status: response.status, data: data || {} }))
  })
}

function errorText(status, data) {
  if (status === 0 || status === 401) return SIGNED_OUT
  if (data && data.errors && data.errors.length) return data.errors.join(' ')
  if (status === 403) return 'You no longer have permission to change this.'
  if (status === 404) return 'This was removed on another screen.'
  return 'The server did not save this. Tap Retry.'
}

// The presigned form POST to the storage bucket, with upload progress (0 to 1).
function postToStorage(presign, file, onProgress) {
  return new Promise((resolve, reject) => {
    const form = new FormData()
    Object.keys(presign.fields || {}).forEach(name => form.append(name, presign.fields[name]))
    form.append('file', file)
    const xhr = new XMLHttpRequest()
    xhr.open('POST', presign.url)
    xhr.upload.onprogress = (event) => { if (event.lengthComputable) onProgress(event.loaded / event.total) }
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error('The file did not reach storage. Tap Retry.')))
    xhr.onerror = () => reject(new TypeError('network'))
    xhr.timeout = UPLOAD_TIMEOUT_MS
    xhr.ontimeout = () => reject(new TypeError('network'))
    xhr.send(form)
  })
}

function stamp() {
  const d = new Date()
  const two = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}${two(d.getMonth() + 1)}${two(d.getDate())}-${two(d.getHours())}${two(d.getMinutes())}${two(d.getSeconds())}`
}

// One file on its way up. Kept for Retry: the shrunk file, and the cached
// file's data once it's in storage (a retry then only attaches it).
class Upload {
  constructor(file, fromCamera) {
    this.original = file
    this.fromCamera = fromCamera
    this.key = {}
    this.file = null
    this.cached = null
    this.tile = null
    this.busy = false
  }
}

export default class extends Controller {
  static targets = ['grid', 'count', 'badge', 'error']

  connect() {
    this.uploads = new Set()
    this.objectUrls = []
    this.onViewerKey = this.onViewerKey.bind(this)
    // The viewer lives on <body>: close it before Turbolinks keeps a copy of
    // the page, or Back would show a dead one.
    this.closeViewer = this.closeViewer.bind(this)
    document.addEventListener('turbolinks:before-cache', this.closeViewer)
  }

  disconnect() {
    document.removeEventListener('turbolinks:before-cache', this.closeViewer)
    this.closeViewer()
    this.objectUrls.forEach(url => URL.revokeObjectURL(url))
    this.objectUrls = []
  }

  // --- adding files ----------------------------------------------------------

  pick(event) {
    const input = event.target
    const files = Array.from(input.files || [])
    const fromCamera = input.hasAttribute('capture')
    input.value = ''
    files.forEach((file) => {
      const upload = new Upload(file, fromCamera)
      this.uploads.add(upload)
      this.enqueue(upload)
    })
  }

  retry(event) {
    const tile = event.currentTarget.closest('.mb-thumb')
    const upload = Array.from(this.uploads).find(u => u.tile === tile)
    if (upload) this.enqueue(upload)
  }

  // Drop a failed upload (the task was signed off, the row removed, or it's
  // simply not wanted), so it no longer counts as not saved.
  discard(event) {
    const tile = event.currentTarget.closest('.mb-thumb')
    const upload = Array.from(this.uploads).find(u => u.tile === tile)
    if (!upload || upload.busy) return

    this.uploads.delete(upload)
    tile.remove()
    this.track(upload.key, Promise.resolve({ discarded: true }))
    this.paintCount()
    this.settle(false)
  }

  enqueue(upload) {
    if (upload.busy) return

    const u = upload
    u.busy = true
    this.paintTile(u, 'uploading', 0)
    const promise = queue.then(() => this.send(u))
    queue = promise
    this.track(u.key, promise, () => this.enqueue(u))
    this.paintBadge()
  }

  track(key, promise, retry) {
    this.element.dispatchEvent(new CustomEvent('mobile-entry:track', { bubbles: true, detail: { key, promise, retry } }))
  }

  // Shrink, presign, store, attach. Resolves { saved, data }; never rejects.
  async send(upload) {
    const u = upload
    try {
      if (!u.file) {
        u.file = await shrink(u.original)
        if (u.fromCamera && u.file.type === 'image/jpeg') u.file = new File([u.file], `photo-${stamp()}.jpg`, { type: 'image/jpeg' })
      }
      if (!u.cached) u.cached = await this.store(u)
      this.paintTile(u, 'uploading', 0.97)
      const { ok, status, data } = await requestJSON(this.data.get('url'), 'POST', { document: JSON.stringify(u.cached) })
      if (!ok || !data.attachment) {
        // The cached copy may be gone (an old retry): send the file again next time.
        if (status === 422) u.cached = null
        throw new Error(errorText(status, data))
      }
      this.uploads.delete(u)
      this.finishTile(u, data.attachment)
      this.settle(true)
      return { saved: true, data }
    } catch (error) {
      u.busy = false
      // fetch and the storage POST fail with a TypeError when offline, and an
      // AbortError when they time out.
      const message = error instanceof TypeError || error.name === 'AbortError' ? 'No connection. Tap Retry.' : error.message
      this.paintTile(u, 'failed')
      this.showError(message || 'The file did not upload. Tap Retry.')
      this.settle(false)
      return { saved: false }
    }
  }

  async store(upload) {
    const { file } = upload
    const url = `${this.data.get('presignUrl')}?filename=${encodeURIComponent(file.name)}&_=${Date.now()}`
    const response = await fetch(url, {
      credentials: 'same-origin', redirect: 'manual', headers: { Accept: 'application/json' }, signal: timeoutSignal(REQUEST_TIMEOUT_MS)
    })
    if (response.type === 'opaqueredirect' || response.redirected || response.status === 401) throw new Error(SIGNED_OUT)
    if (!response.ok) throw new Error('The upload could not start. Tap Retry.')
    const presign = await response.json()

    await postToStorage(presign, file, share => this.paintTile(upload, 'uploading', share * 0.95))
    return {
      id: presign.fields.key.replace(/^cache\//, ''),
      storage: 'cache',
      metadata: { size: file.size, filename: file.name, mime_type: file.type }
    }
  }

  // --- tiles -------------------------------------------------------------------

  // The tile for an upload: made on first use, with the photo (or a file icon).
  paintTile(upload, state, share) {
    const u = upload
    if (!u.tile) {
      u.tile = document.createElement('div')
      u.tile.className = 'mb-thumb'
      u.tile.setAttribute('role', 'img')
      const isImage = /^image\//.test(u.original.type) && !HEIC.test(u.original.name)
      if (isImage) {
        const url = URL.createObjectURL(u.original)
        this.objectUrls.push(url)
        const image = document.createElement('img')
        image.alt = ''
        image.src = url
        u.tile.appendChild(image)
      } else {
        u.tile.classList.add('mb-thumb--doc')
        u.tile.insertAdjacentHTML('beforeend', `${FILE_ICON}<i></i>`)
        u.tile.querySelector('i').textContent = u.original.name
      }
      u.tile.insertAdjacentHTML('beforeend', '<span class="mb-thumb-bar"><span></span></span><button type="button" class="mb-retry mb-thumb-retry" data-action="mobile-photos#retry">Not saved · Retry</button><button type="button" class="mb-thumb-remove" data-action="mobile-photos#discard" aria-label="Remove this upload">×</button>')
      this.gridTarget.appendChild(u.tile)
      this.gridTarget.hidden = false
    }
    u.tile.classList.toggle('is-uploading', state === 'uploading')
    u.tile.classList.toggle('is-failed', state === 'failed')
    u.tile.setAttribute('aria-label', `${u.original.name}: ${state === 'failed' ? 'not saved' : 'uploading'}`)
    if (state === 'uploading') u.tile.querySelector('.mb-thumb-bar span').style.width = `${Math.round((share || 0) * 100)}%`
  }

  // The uploaded file's tile becomes a thumbnail like the server's (_thumbs),
  // keeping the photo already on screen.
  finishTile(upload, file) {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = `mb-thumb${file.image ? '' : ' mb-thumb--doc'}`
    button.title = file.name
    button.setAttribute('aria-label', `Open ${file.name}`)
    button.dataset.file = JSON.stringify(file)
    button.dataset.action = 'mobile-photos#view'
    const local = upload.tile.querySelector('img')
    if (file.image) {
      const image = document.createElement('img')
      image.alt = ''
      image.src = local ? local.src : file.thumb
      button.appendChild(image)
    } else {
      button.insertAdjacentHTML('beforeend', `${FILE_ICON}<i></i>`)
      button.querySelector('i').textContent = file.name
    }
    upload.tile.replaceWith(button)
    this.paintCount()
  }

  // --- painting -----------------------------------------------------------------

  thumbs() {
    return Array.from(this.gridTarget.querySelectorAll('[data-file]'))
  }

  paintCount() {
    if (!this.hasCountTarget) return

    const n = this.thumbs().length
    this.countTarget.textContent = n ? `${n} file${n === 1 ? '' : 's'}` : 'None'
    this.gridTarget.hidden = this.gridTarget.children.length === 0
  }

  busyCount() {
    return Array.from(this.uploads).filter(u => u.busy).length
  }

  paintBadge(state) {
    if (!this.hasBadgeTarget) return

    const badge = this.badgeTarget
    if (this.fadeTimer) clearTimeout(this.fadeTimer)
    const busy = this.busyCount()
    if (busy) {
      badge.className = 'mb-save mb-save--saving'
      badge.textContent = `Uploading ${busy === 1 ? '1 file' : `${busy} files`}…`
    } else if (state === 'saved') {
      badge.className = 'mb-save mb-save--saved'
      badge.textContent = 'Saved ✓'
      this.fadeTimer = setTimeout(() => { badge.textContent = '' }, SAVED_FADE_MS)
    } else {
      badge.className = 'mb-save'
      badge.textContent = ''
    }
  }

  settle(saved) {
    const failed = this.element.querySelector('.mb-thumb.is-failed')
    if (!failed) this.hideError()
    this.paintBadge(saved && !failed ? 'saved' : null)
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  hideError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.hidden = true
    this.errorTarget.textContent = ''
  }

  // --- viewer --------------------------------------------------------------------

  view(event) {
    const index = this.thumbs().indexOf(event.currentTarget)
    if (index < 0) return

    this.openViewer()
    this.showFile(index)
  }

  openViewer() {
    if (this.viewer) return

    const viewer = document.createElement('div')
    viewer.className = 'mb-viewer'
    viewer.setAttribute('role', 'dialog')
    viewer.setAttribute('aria-modal', 'true')
    viewer.setAttribute('aria-label', 'Files')
    viewer.innerHTML = `
      <div class="mb-viewer-top">
        <button type="button" data-viewer="close">Close</button>
        <span data-viewer="counter"></span>
        <button type="button" data-viewer="delete" class="mb-viewer-delete">Delete</button>
      </div>
      <div class="mb-viewer-stage" data-viewer="stage"></div>
      <p class="mb-viewer-error" data-viewer="error" hidden></p>
      <div class="mb-viewer-bottom">
        <button type="button" data-viewer="prev" aria-label="Previous file">‹</button>
        <span data-viewer="name"></span>
        <button type="button" data-viewer="next" aria-label="Next file">›</button>
      </div>`
    viewer.addEventListener('click', (event) => {
      const button = event.target.closest('[data-viewer]')
      const action = button && button.dataset.viewer
      if (action === 'close') this.closeViewer()
      else if (action === 'prev') this.showFile(this.shownIndex() - 1)
      else if (action === 'next') this.showFile(this.shownIndex() + 1)
      else if (action === 'delete') this.deleteShown()
    })
    const stage = viewer.querySelector('[data-viewer="stage"]')
    stage.addEventListener('touchstart', (event) => { this.touchX = event.changedTouches[0].clientX }, { passive: true })
    stage.addEventListener('touchend', (event) => {
      const dx = event.changedTouches[0].clientX - (this.touchX || 0)
      if (Math.abs(dx) > SWIPE_PX) this.showFile(this.shownIndex() + (dx < 0 ? 1 : -1))
    })
    document.addEventListener('keydown', this.onViewerKey)
    document.body.appendChild(viewer)
    document.body.classList.add('mb-sheet-open')
    this.viewer = viewer
    viewer.querySelector('[data-viewer="close"]').focus()
  }

  closeViewer() {
    if (!this.viewer) return

    document.removeEventListener('keydown', this.onViewerKey)
    this.viewer.remove()
    this.viewer = null
    document.body.classList.remove('mb-sheet-open')
  }

  onViewerKey(event) {
    if (event.key === 'Escape') this.closeViewer()
    else if (event.key === 'ArrowLeft') this.showFile(this.shownIndex() - 1)
    else if (event.key === 'ArrowRight') this.showFile(this.shownIndex() + 1)
  }

  // Where the file on screen is now: an upload finishing earlier in the grid
  // moves it along.
  shownIndex() {
    const index = this.thumbs().indexOf(this.viewerThumb)
    return index < 0 ? this.viewerIndex : index
  }

  part(name) {
    return this.viewer.querySelector(`[data-viewer="${name}"]`)
  }

  showFile(index) {
    const thumbs = this.thumbs()
    if (!this.viewer) return
    if (!thumbs.length) { this.closeViewer(); return }

    this.viewerIndex = Math.max(0, Math.min(index, thumbs.length - 1))
    this.viewerThumb = thumbs[this.viewerIndex]
    const file = JSON.parse(this.viewerThumb.dataset.file)
    const stage = this.part('stage')
    stage.innerHTML = ''
    if (file.image) {
      const image = document.createElement('img')
      image.alt = file.name
      image.src = file.url
      stage.appendChild(image)
    } else {
      stage.innerHTML = `<div class="mb-viewer-doc">${FILE_ICON}<b></b><a target="_blank" rel="noopener">Open file</a></div>`
      stage.querySelector('b').textContent = file.name
      stage.querySelector('a').href = file.url
    }
    this.part('counter').textContent = `${this.viewerIndex + 1} of ${thumbs.length}`
    this.part('name').textContent = file.name
    this.part('prev').disabled = this.viewerIndex === 0
    this.part('next').disabled = this.viewerIndex === thumbs.length - 1
    const remove = this.part('delete')
    remove.hidden = !file.delete_url
    remove.disabled = false
    remove.textContent = 'Delete'
    this.part('error').hidden = true
  }

  deleteShown() {
    const thumb = this.viewerThumb
    const file = thumb && JSON.parse(thumb.dataset.file)
    if (!file || !file.delete_url) return
    if (!window.confirm(`Delete ${file.name}? It is removed from this result everywhere.`)) return

    const remove = this.part('delete')
    remove.disabled = true
    remove.textContent = 'Deleting…'
    const shown = this.viewer
    const promise = requestJSON(file.delete_url, 'DELETE').then(({ ok, status, data }) => {
      if (!ok || !data.deleted) throw new Error(errorText(status, data))
      const index = this.thumbs().indexOf(thumb)
      thumb.remove()
      this.paintCount()
      this.paintBadge('saved')
      if (this.viewer === shown) this.showFile(this.viewerThumb === thumb ? index : this.shownIndex())
      return { saved: true, data }
    }).catch((error) => {
      if (this.viewer === shown) {
        const message = error instanceof TypeError || error.name === 'AbortError' ? 'No connection. Try again.' : error.message
        this.part('error').textContent = `Not deleted. ${message}`
        this.part('error').hidden = false
        remove.disabled = false
        remove.textContent = 'Delete'
      }
      // A delete that didn't happen leaves nothing unsaved to retry.
      return {}
    })
    this.track({}, promise)
  }
}
